import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { BillingDetailsApiData } from 'src/app/pages/new-billing/types/api.types';
import { BillingActionsService } from 'src/app/services/billing/billing-actions.service';
import { provideAmpTrackCtx } from 'src/app/services/tracking/amp-track';
import { TrialOfferService } from 'src/app/services/trial-offer/trial-offer.service';

export type TrialOfferCancelDialogData = {
  billingDetails: BillingDetailsApiData;
};

export function openTrialOfferCancelDialog(dialog: MatDialog, billingDetails: BillingDetailsApiData) {
  return dialog.open<TrialOfferCancelDialogComponent, TrialOfferCancelDialogData, boolean>(TrialOfferCancelDialogComponent, {
    data: { billingDetails },
    width: '432px',
    maxWidth: '92vw',
    autoFocus: false,
  });
}

@Component({
  selector: 'app-trial-offer-cancel-dialog',
  templateUrl: './trial-offer-cancel-dialog.component.html',
  styleUrls: ['./trial-offer-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideAmpTrackCtx('billing')],
})
export class TrialOfferCancelDialogComponent {
  readonly trialExpiry: string | null;
  readonly paidPlanName: string;
  submitting = false;
  errorKey: string | null = null;

  constructor(
    private readonly dialogRef: MatDialogRef<TrialOfferCancelDialogComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) data: TrialOfferCancelDialogData,
    private readonly trialOffer: TrialOfferService,
    private readonly billingActions: BillingActionsService,
    private readonly translate: TranslateService,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.trialExpiry = data.billingDetails?.trialOffer?.trialExpiry ?? null;
    this.paidPlanName = trialOffer.getPaidPlanName(data.billingDetails)
      ?? translate.instant('trialOffer.cancel.currentPlanFallback');
  }

  onConfirm(): void {
    if (this.submitting) {
      return;
    }
    this.submitting = true;
    this.errorKey = null;
    this.cdr.markForCheck();

    this.trialOffer.cancel().subscribe({
      next: () => {
        this.billingActions.openSnackbar({ message: this.translate.instant('trialOffer.snackbar.cancelled') });
        this.dialogRef.close(true);
      },
      error: (error: HttpErrorResponse) => {
        this.submitting = false;
        this.errorKey = `trialOffer.errors.${this.trialOffer.readErrorCode(error) ?? 'cancelUnknown'}`;
        this.cdr.markForCheck();
      },
    });
  }

  onKeep(): void {
    this.dialogRef.close(false);
  }
}
